<template>
  <v-card>
    <v-card-title class="primary">
      <v-row no-gutters align="center">
        <v-col>
          <span class="headline white--text">{{ readonly ? $t('location') : $t('setBidLocation') }}</span>
        </v-col>
        <v-col cols="auto">
          <Icon
          icon="mdi-close"
          iconColor="white"
          :small="false"
          large
          :tooltipText="$t('close')"
          @icon-clicked="$emit('close')"/>
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-text>
      <v-row align="center">
        <v-col cols="12" class="mt-6">
          <v-card flat style="margin-bottom: -1px;">
            <div id="bid-location-map" style="height: 60vh"></div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-text-field
          v-model="latitudeTextField"
          :label="$t('latitude')"
          color="black"
          :readonly="readonly"
          @blur="onTextFieldBlur"
          :rules="[rules.required, rules.decimal, rules.validLatitudeForTextBox, rules.sixDecimalPlacesOrFewer]"
          type="number"
          class="mb-n4"
          hide-spin-buttons/>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-text-field
          v-model="longitudeTextField"
          :label="$t('longitude')"
          color="black"
          :readonly="readonly"
          @blur="onTextFieldBlur"
          :rules="[rules.required, rules.decimal, rules.validLongitudeForTextBox, rules.sixDecimalPlacesOrFewer]"
          type="number"
          class="mb-n4"
          hide-spin-buttons/>
        </v-col>
        <v-col cols="auto" v-if="!readonly">
          <v-btn
          :disabled="!showApplyButton"
          class="mr-3 mt-n2 mb-n4 primary"
          @click="applyLocation">
            {{ $t('apply') }}
          </v-btn>
        </v-col>
        <v-spacer/>
        <v-col cols="auto" v-if="locationLabel" class="mb-n4">
          <span class="text--secondary">
            <v-icon small color="primary">mdi-map-marker</v-icon>{{ locationLabel }}
          </span>
        </v-col>
        <v-col cols="auto" class="mb-n4" v-if="!readonly">
          <v-btn
          :disabled="!enableSubmitButton"
          class="primary mt-n2"
          @click="submitLocation">
            {{ $t('submit') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import * as atlas from 'azure-maps-control'
import { mapActions } from 'vuex'
import fieldRules from '@/utils/rules'
import { getFormattedCoord, getFormattedCoordWithTrailingZeroes } from '@/utils/NumericMutations'
import { mapResizeTimeout, azureMapsApiKey, defaultCoordinates, coordinatesInBounds, disposeMap } from '@/utils/map/MapHelpers'

export default {
  name: 'BidLocationMapDialog',

  components: {
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    propLocation: {
      type: Object,
      default: undefined
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    rules: fieldRules.rules,
    map: undefined,
    marker: undefined,
    spot: {
      latitude: defaultCoordinates.latitude,
      longitude: defaultCoordinates.longitude,
      countrySubdivision: undefined,
      countrySecondarySubdivision: undefined
    },
    latitudeTextField: undefined,
    longitudeTextField: undefined
  }),

  computed: {
    showApplyButton () {
      const lat = parseFloat(this.latitudeTextField)
      const lon = parseFloat(this.longitudeTextField)
      if (!coordinatesInBounds(lat, lon)) return false
      return this.spot.latitude !== lat || this.spot.longitude !== lon
    },

    enableSubmitButton () {
      return coordinatesInBounds(parseFloat(this.latitudeTextField), parseFloat(this.longitudeTextField))
    },

    locationLabel () {
      const { countrySecondarySubdivision: county, countrySubdivision: state } = this.spot
      return county && state ? `${county}, ${state}` : ''
    }
  },

  watch: {
    spot: {
      handler (val) {
        this.latitudeTextField = getFormattedCoordWithTrailingZeroes(val.latitude)
        this.longitudeTextField = getFormattedCoordWithTrailingZeroes(val.longitude)
      },
      deep: true
    },

    '$vuetify.breakpoint.height' () { this.onResize() },
    '$vuetify.breakpoint.width' () { this.onResize() }
  },

  mounted () {
    if (typeof this.propLocation?.latitude === 'number' && typeof this.propLocation?.longitude === 'number') {
      this.spot = { ...this.spot, ...this.propLocation }
    }
    this.updateCoordinateTextBoxes()
    setTimeout(() => this.initMap(), 100)
  },

  beforeDestroy () {
    if (this.map) {
      disposeMap(this.map)
      this.map = null
    }
  },

  methods: {
    ...mapActions('tract', ['getLocationWithCoordinate']),

    submitLocation () {
      this.$emit('selected-location', { ...this.spot })
    },

    async applyLocation () {
      this.spot = {
        ...this.spot,
        latitude: getFormattedCoord(this.latitudeTextField),
        longitude: getFormattedCoord(this.longitudeTextField)
      }
      this.setMarkerLocation()
      this.setCamera([this.spot.longitude, this.spot.latitude])
      await this.reverseGeocode()
    },

    setMarkerLocation () {
      if (this.marker && this.map) {
        this.marker.setOptions({ position: [this.spot.longitude, this.spot.latitude] })
      }
    },

    initMap () {
      this.map = new atlas.Map('bid-location-map', {
        center: [this.spot.longitude, this.spot.latitude],
        zoom: 10,
        view: 'Auto',
        autoResize: true,
        style: 'satellite_road_labels',
        authOptions: {
          authType: 'subscriptionKey',
          subscriptionKey: azureMapsApiKey()
        }
      })

      this.map.events.add('ready', () => this.addMarker())
    },

    addMarker () {
      this.marker = new atlas.HtmlMarker({
        draggable: !this.readonly,
        position: [this.spot.longitude, this.spot.latitude],
        pixelOffset: [0, 0],
        color: '#D15F27'
      })
      this.map.events.add('dragend', this.marker, (e) => {
        this.handleDrag(e.target.marker._lngLat)
      })
      this.map.markers.add(this.marker)
      this.setCamera([this.spot.longitude, this.spot.latitude])
    },

    async handleDrag (coordinate) {
      this.spot = {
        ...this.spot,
        latitude: parseFloat(coordinate.lat.toFixed(6)),
        longitude: parseFloat(coordinate.lng.toFixed(6))
      }
      this.setCamera([this.spot.longitude, this.spot.latitude])
      await this.reverseGeocode()
    },

    async reverseGeocode () {
      try {
        const result = await this.getLocationWithCoordinate({
          latitude: this.spot.latitude,
          longitude: this.spot.longitude
        })
        if (result) {
          this.spot = {
            ...this.spot,
            countrySubdivision: result.countrySubdivision,
            countrySecondarySubdivision: result.countrySecondarySubdivision
          }
        }
      } catch (error) {
        // Leave the previously resolved county/state in place if the lookup fails.
      }
    },

    onResize () {
      if (this.map) setTimeout(() => this.map.map.resize(), mapResizeTimeout)
    },

    setCamera (coordinate) {
      this.map.setCamera({ center: coordinate, type: 'ease', duration: 350 })
    },

    updateCoordinateTextBoxes () {
      this.latitudeTextField = getFormattedCoordWithTrailingZeroes(this.spot.latitude)
      this.longitudeTextField = getFormattedCoordWithTrailingZeroes(this.spot.longitude)
    },

    onTextFieldBlur () {
      if (this.rules.validLatitudeForTextBox(this.latitudeTextField) === true || Math.abs(parseFloat(this.latitudeTextField)) <= 90) {
        this.latitudeTextField = getFormattedCoordWithTrailingZeroes(this.latitudeTextField)
      }
      if (this.rules.validLongitudeForTextBox(this.longitudeTextField) === true || Math.abs(parseFloat(this.longitudeTextField)) <= 180) {
        this.longitudeTextField = getFormattedCoordWithTrailingZeroes(this.longitudeTextField)
      }
    }
  }
}
</script>
